/**
 * crypto.js
 * AES-256-CBC encryption that matches the Java backend CryptoService exactly.
 *
 * Java:  AES/CBC/PKCS5PADDING, random 16-byte IV
 * Output format:  base64(iv) + ":" + base64(encrypted)
 *
 * Uses the browser's built-in Web Crypto API — no extra npm package needed.
 */

// The key must match crypto.key in application-local.yaml / application.yaml
// Keep this in a .env variable so it is never committed to git.
const CRYPTO_KEY = process.env.REACT_APP_CRYPTO_KEY || "VZv1xkCth6g1MAiT8Ool0yZSW4bm0de1";

/**
 * Encrypt a plain-text string with AES-256-CBC.
 * Returns  base64(randomIV) + ":" + base64(ciphertext)
 *
 * @param {string} plainText  - e.g. the raw password
 * @returns {Promise<string>} - encrypted string ready to send to the backend
 */
export async function encryptAES(plainText) {
  try {
    // 1. Import the key (must be exactly 16, 24 or 32 UTF-8 bytes → 128/192/256-bit)
    const keyBytes = new TextEncoder().encode(CRYPTO_KEY);
    const cryptoKey = await window.crypto.subtle.importKey(
      "raw",
      keyBytes,
      { name: "AES-CBC" },
      false,
      ["encrypt"]
    );

    // 2. Generate a random 16-byte IV (matches Java: new byte[16]; secureRandom.nextBytes(iv))
    const iv = window.crypto.getRandomValues(new Uint8Array(16));

    // 3. Encrypt  (Web Crypto uses PKCS#7 padding, which is identical to PKCS#5 for 16-byte blocks)
    const dataBytes = new TextEncoder().encode(plainText);
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      { name: "AES-CBC", iv },
      cryptoKey,
      dataBytes
    );

    // 4. Base64-encode both parts and join with ":"
    const ivBase64        = btoa(String.fromCharCode(...iv));
    const encryptedBase64 = btoa(
      String.fromCharCode(...new Uint8Array(encryptedBuffer))
    );

    return `${ivBase64}:${encryptedBase64}`;
  } catch (err) {
    console.error("Encryption failed:", err);
    // Fallback: return plain text so the backend's plain-password fallback handles it
    return plainText;
  }
}
